import { useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, BookOpen, PackageCheck, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { SectionHeader } from '@/components/ui/section-header';
import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScoreBreakdown } from '@/components/local-potential/ScoreBreakdown';
import { OwnChannelsBoost } from '@/components/local-potential/OwnChannelsBoost';
import { ClaimableWinCard } from '@/components/local-potential/ClaimableWinCard';
import { SuggestionCard } from '@/components/local-potential/SuggestionCard';
import { HiddenTray } from '@/components/local-potential/HiddenTray';
import { CopyStorefrontButton } from '@/components/local-potential/CopyStorefrontButton';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useLocalPotential } from '@/hooks/useLocalPotential';
import { useLocalPotentialActions } from '@/hooks/useLocalPotentialActions';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { GROUP_META, TARGET_SCORE, presentationFor } from '@/data/localPotential';

const FILTERS = [
	{ id: 'all', label: 'All' },
	{ id: 'channels', label: 'Channels', dot: 'bg-primary', active: 'bg-primary text-primary-foreground border-primary' },
	{ id: 'tools', label: 'Tools', dot: 'bg-action', active: 'bg-action text-white border-action' },
	{ id: 'links', label: 'Links', dot: 'bg-success', active: 'bg-success text-white border-success' },
	{ id: 'products', label: 'Products', dot: 'bg-warning', active: 'bg-warning text-white border-warning' }
];

const bucketActive = (group) =>
	Math.min(
		group.max,
		group.items.filter((i) => i.activated).reduce((sum, i) => sum + i.uplift, 0)
	);
const emptyGroup = (id) => ({ id, max: 0, items: [] });
const scrollTo = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

// Coming-soon Tools (NEA-5935) — presentation-only cards appended to the Tools
// section. Non-scoring (absent from the backend resolver / weights.json), so they
// never affect the score; ported from the fresh-dashboard design.
const COMING_SOON_TOOLS = [
	{ id: 'click-collect', activated: false, uplift: 0, comingSoon: true },
	{ id: 'product-page-linking', activated: false, uplift: 0, comingSoon: true }
];

const BackButton = ({ onBack }) => (
	<button
		type="button"
		onClick={onBack}
		className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
	>
		<ArrowLeft className="h-3 w-3" /> Back
	</button>
);

function FilterPills({ active, onChange }) {
	return (
		<div className="space-y-3">
			<h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Improvements by area</h2>
			<nav aria-label="Filter sections" className="flex flex-wrap gap-2">
				{FILTERS.map((f) => {
					const isActive = active === f.id;
					return (
						<button
							key={f.id}
							type="button"
							onClick={() => onChange(f.id)}
							className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
								isActive
									? (f.active ?? 'border-foreground bg-foreground text-background')
									: 'border-border bg-accent/40 text-foreground hover:bg-accent'
							}`}
						>
							{f.dot ? <span aria-hidden className={`h-2 w-2 rounded-full ${isActive ? 'bg-white/90' : f.dot}`} /> : null}
							{f.label}
						</button>
					);
				})}
			</nav>
		</div>
	);
}

function GroupSection({ id, group, tone, eyebrowPill, dismissed, onHide, onRestore, shopPath, showMoreChannels }) {
	const meta = GROUP_META[id] ?? { eyebrow: id, title: id, sub: '' };
	const isHidden = (item) => dismissed.has(`suggestion:${item.id}`);
	const visible = group.items.filter((item) => item.activated || !isHidden(item));
	const hidden = group.items.filter((item) => !item.activated && isHidden(item));
	return (
		<section id={`lp-${id}`} className="scroll-mt-24">
			<SectionHeader eyebrow={meta.eyebrow} eyebrowPill={eyebrowPill} title={meta.title} sub={meta.sub} />
			<div className="space-y-3">
				{visible.map((item) => (
					<SuggestionCard
						key={item.id}
						item={{ ...item, group: id }}
						tone={tone}
						shopPath={shopPath}
						onHide={!item.activated ? () => onHide(item.id) : undefined}
					/>
				))}
				<HiddenTray count={hidden.length}>
					{hidden.map((item) => (
						<SuggestionCard
							key={item.id}
							item={{ ...item, group: id }}
							tone={tone}
							shopPath={shopPath}
							onUnhide={() => onRestore(item.id)}
						/>
					))}
				</HiddenTray>
				{showMoreChannels ? (
					<div className="pt-1">
						<Link
							to={shopPath('/channels')}
							className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
						>
							More channels <ArrowRight className="h-3.5 w-3.5" />
						</Link>
					</div>
				) : null}
			</div>
		</section>
	);
}

function LinksSection({ links, storefrontUrl, dismissed, claimWin, unclaimWin, onHide, onRestore, shopPath }) {
	const item = (id) => links.items.find((i) => i.id === id) ?? { id, activated: false, uplift: 0 };
	const social = item('storefrontLinkOnSocials');
	const profile = item('storefrontProfileComplete');
	const wins = [item('emailButton'), item('appleBusinessConnect'), item('shopWindowQrCode')];

	const cards = [
		{
			id: social.id,
			activated: social.activated,
			render: (mode) => (
				<OwnChannelsBoost
					activated={social.activated}
					uplift={social.uplift || 3}
					onToggle={() => (social.activated ? unclaimWin(social.id) : claimWin(social.id))}
					hidden={mode === 'hidden'}
					onHide={() => onHide(social.id)}
					onUnhide={() => onRestore(social.id)}
				/>
			)
		},
		{
			id: profile.id,
			activated: profile.activated,
			render: (mode) => (
				<SuggestionCard
					item={{ ...profile, group: 'links' }}
					tone="success"
					shopPath={shopPath}
					onHide={mode === 'visible' && !profile.activated ? () => onHide(profile.id) : undefined}
					onUnhide={mode === 'hidden' ? () => onRestore(profile.id) : undefined}
				/>
			)
		},
		...wins.map((win) => {
			const presentation = presentationFor(win.id);
			return {
				id: win.id,
				activated: win.activated,
				render: (mode) => (
					<ClaimableWinCard
						icon={presentation.icon}
						title={presentation.title}
						body={presentation.body}
						uplift={win.uplift || 2}
						activated={win.activated}
						onToggle={() => (win.activated ? unclaimWin(win.id) : claimWin(win.id))}
						onHide={mode === 'visible' && !win.activated ? () => onHide(win.id) : undefined}
						onUnhide={mode === 'hidden' ? () => onRestore(win.id) : undefined}
					/>
				)
			};
		})
	];

	const isHidden = (card) => !card.activated && dismissed.has(`suggestion:${card.id}`);
	const visible = cards.filter((card) => !isHidden(card));
	const hidden = cards.filter((card) => isHidden(card));

	return (
		<section id="lp-links" className="scroll-mt-24">
			<SectionHeader
				eyebrow="Links"
				eyebrowPill
				title={GROUP_META.links.title}
				sub={GROUP_META.links.sub}
				rightSlot={
					<div className="flex flex-col items-stretch gap-2">
						<Link
							to={shopPath('/learn/unified-commerce')}
							className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
						>
							<BookOpen className="h-3.5 w-3.5" /> Learn about Unified Commerce
						</Link>
						<CopyStorefrontButton url={storefrontUrl} />
					</div>
				}
			/>
			<div className="space-y-3">
				{visible.map((card) => (
					<div key={card.id}>{card.render('visible')}</div>
				))}
				<HiddenTray count={hidden.length}>
					{hidden.map((card) => (
						<div key={card.id}>{card.render('hidden')}</div>
					))}
				</HiddenTray>
			</div>
		</section>
	);
}

function ProductsSection({ productVisible, uplift, shopPath }) {
	const total = productVisible.total ?? 0;
	const visible = productVisible.visible ?? 0;
	const invisible = Math.max(0, total - visible);
	const pct = Math.round((productVisible.pct ?? 0) * 100);
	return (
		<section id="lp-products" className="scroll-mt-24">
			<SectionHeader
				eyebrow="Products"
				eyebrowPill="warning"
				title="Improve your product profiles & stock accuracy"
				sub={`The richer your product data, the more of your catalogue every channel will show. Today, ${pct}% of your in-stock products are visible across channels.`}
			/>
			<div className="space-y-3">
				{invisible > 0 ? (
					<SoftCard className="relative !p-5 md:!p-6">
						<div className="flex items-start gap-4">
							<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground">
								<AlertTriangle className="h-5 w-5" />
							</div>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-baseline gap-2">
									<span className="text-[15px] font-bold tabular-nums text-foreground md:text-base">
										{invisible.toLocaleString('en-GB')}
									</span>
									<span className="text-[15px] font-bold text-foreground md:text-base">
										(of {total.toLocaleString('en-GB')}) in-stock products are invisible
									</span>
									{uplift > 0 ? (
										<span className="inline-flex items-center gap-1 self-center rounded-full border border-warning/40 bg-white px-2.5 py-1 text-xs font-semibold text-warning">
											<span aria-hidden className="h-2 w-2 rounded-full bg-warning" /> +{uplift}%
										</span>
									) : null}
								</div>
								<p className="mt-1 text-sm text-muted-foreground">
									Add titles to these products so local shoppers can find them.
								</p>
							</div>
							<Link
								to={shopPath('/inventory')}
								className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
							>
								Fix now <ArrowRight className="h-3.5 w-3.5" />
							</Link>
						</div>
					</SoftCard>
				) : null}
				<SoftCard className="!p-5 md:!p-6">
					<div className="flex items-start gap-4">
						<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground">
							<Sparkles className="h-5 w-5" />
						</div>
						<div className="min-w-0 flex-1">
							<h3 className="text-[15px] font-bold text-foreground md:text-base">Enrich your product profiles</h3>
							<p className="mt-0.5 text-sm text-muted-foreground">
								Adding images, brand and category info increases customer trust and your chances of showing up.
							</p>
						</div>
						<Link
							to={shopPath('/inventory')}
							className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
						>
							Improve product data <ArrowRight className="h-3.5 w-3.5" />
						</Link>
					</div>
				</SoftCard>
				<SoftCard className="!p-5 md:!p-6">
					<div className="flex items-start gap-4">
						<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground">
							<PackageCheck className="h-5 w-5" />
						</div>
						<div className="min-w-0 flex-1">
							<h3 className="text-[15px] font-bold text-foreground md:text-base">Keep your stock accuracy high</h3>
							<p className="mt-0.5 text-sm text-muted-foreground">
								Customers return if you show products they trust are in stock.
							</p>
						</div>
						<Link
							to={shopPath('/inventory/connection')}
							className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
						>
							Check accuracy <ArrowRight className="h-3.5 w-3.5" />
						</Link>
					</div>
				</SoftCard>
			</div>
		</section>
	);
}

/**
 * Local Potential breakdown page (NEA-5765 / NEA-5869). Ported from the
 * fresh-dashboard design — hero, ScoreBreakdown (live gauge × product
 * visibility), "improvements by area" filters, and the Links / Channels / Tools
 * / Products sections — but bound to the backend resolver (`useLocalPotential`)
 * rather than the design's mock data. Self-attested wins persist server-side via
 * `useLocalPotentialActions`; hides persist in `dismissedHints`.
 */
export function LocalPotential() {
	const navigate = useNavigate();
	const { shop } = useShop();
	const { shopPath } = useShopPath();
	const { data, isLoading } = useLocalPotential();
	const { claimWin, unclaimWin, dismiss, restore } = useLocalPotentialActions();
	const { billing, isLoading: billingLoading } = useBillingStatus(shop?.id);
	const [activeFilter, setActiveFilter] = useState('all');

	if (isLoading || !data) return <LocalPotentialSkeleton onBack={() => navigate(-1)} />;

	// Match the Overview page's `isConverted` window (NEA-5836): trial-based
	// journeys anchor on `trialEnd`; variant-C uses `subscriptionStart + 30d`.
	const thirtyDaysEnd =
		billing?.trialEnd ??
		(billing?.subscriptionStart
			? new Date(new Date(billing.subscriptionStart).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
			: null);
	const isConverted = thirtyDaysEnd != null && new Date(thirtyDaysEnd) < new Date();

	const byId = Object.fromEntries(data.groups.map((g) => [g.id, g]));
	const channels = byId.channels ?? emptyGroup('channels');
	const tools = byId.tools ?? emptyGroup('tools');
	const links = byId.links ?? emptyGroup('links');
	const productVisible = data.productVisible ?? { visible: 0, total: 0, pct: 0 };

	const switchedOnPct = Math.min(100, data.baseline + data.earned);
	const readyToShowPct = Math.round((productVisible.pct ?? 0) * 100);
	// Score headroom from making the currently-invisible products visible: earned ×
	// the unrealised visibility fraction (score = baseline + earned × pct).
	const productHeadroom = Math.round((data.earned ?? 0) * (1 - (productVisible.pct ?? 0)));
	const dismissed = new Set(shop?.dismissedHints ?? []);
	const storefrontUrl = shop?.swisUrl || (shop?.slug ? `https://${shop.slug}.near.st` : '');

	const onHide = (id) => dismiss(`suggestion:${id}`);
	const onRestore = (id) => restore(`suggestion:${id}`);
	const show = (id) => activeFilter === 'all' || activeFilter === id;

	return (
		<div className="space-y-12">
			<header>
				<BackButton onBack={() => navigate(-1)} />
				<h1 className="mt-1 text-3xl font-extrabold tracking-tight md:text-[34px]">
					Unlock the <span className="text-gradient-unify">full local potential</span> of your store
				</h1>
				<p className="mt-2 max-w-2xl text-base text-muted-foreground">
					With your in-store inventory linked to Unify, your products are visible to more local shoppers than ever. Your Local
					Potential Score tracks how easily locals can discover your products. Activate more channels, add more product data, and
					push it higher.
				</p>
			</header>

			<ScoreBreakdown
				score={data.score}
				target={TARGET_SCORE}
				channels={{ active: bucketActive(channels), max: channels.max }}
				tools={{ active: bucketActive(tools), max: tools.max }}
				links={{ active: bucketActive(links), max: links.max }}
				switchedOnPct={switchedOnPct}
				readyToShowPct={readyToShowPct}
				onFixProducts={scrollTo('lp-products')}
				isConverted={isConverted}
				billingLoading={billingLoading}
			/>

			<FilterPills active={activeFilter} onChange={setActiveFilter} />

			{show('links') ? (
				<LinksSection
					links={links}
					storefrontUrl={storefrontUrl}
					dismissed={dismissed}
					claimWin={claimWin}
					unclaimWin={unclaimWin}
					onHide={onHide}
					onRestore={onRestore}
					shopPath={shopPath}
				/>
			) : null}
			{show('channels') ? (
				<GroupSection
					id="channels"
					group={channels}
					tone="primary"
					eyebrowPill="primary"
					dismissed={dismissed}
					onHide={onHide}
					onRestore={onRestore}
					shopPath={shopPath}
					showMoreChannels
				/>
			) : null}
			{show('tools') ? (
				<GroupSection
					id="tools"
					group={{ ...tools, items: [...tools.items, ...COMING_SOON_TOOLS] }}
					tone="action"
					eyebrowPill="action"
					dismissed={dismissed}
					onHide={onHide}
					onRestore={onRestore}
					shopPath={shopPath}
				/>
			) : null}
			{show('products') ? <ProductsSection productVisible={productVisible} uplift={productHeadroom} shopPath={shopPath} /> : null}
		</div>
	);
}

function LocalPotentialSkeleton({ onBack }) {
	return (
		<div className="space-y-12">
			<header>
				<BackButton onBack={onBack} />
				<Skeleton className="mt-3 h-9 w-96 max-w-full" />
				<Skeleton className="mt-3 h-4 w-full max-w-2xl" />
			</header>
			<Skeleton className="h-64 rounded-2xl" />
			<div className="space-y-3">
				<Skeleton className="h-5 w-48" />
				{[0, 1, 2].map((i) => (
					<Skeleton key={i} className="h-24 rounded-2xl" />
				))}
			</div>
		</div>
	);
}
